// Cave84 — Notifications service (Phase 7 Week 19)
// Spec §4.15 F15. In-app only — no push notifications.
// Collection: notifications/{notificationId}

import {
  addDoc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import type { Notification, NotificationType } from '@cave84/shared-types';
import { db } from './firebase';
import { typedCollection, typedDoc } from './firestore';

const notifCol = () => typedCollection<Notification>('notifications');

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

/** Most recent 50 notifications for the current user. */
export async function listNotifications(userId: string): Promise<Notification[]> {
  const snap = await getDocs(
    query(
      notifCol(),
      where('user_id', '==', userId),
      orderBy('created_at', 'desc'),
      limit(50)
    )
  );
  return snap.docs.map(d => d.data());
}

/** Unread count — used for the badge. */
export async function countUnread(userId: string): Promise<number> {
  const snap = await getDocs(
    query(
      notifCol(),
      where('user_id', '==', userId),
      where('read', '==', false)
    )
  );
  return snap.size;
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

/** Mark a single notification as read. */
export async function markRead(id: string): Promise<void> {
  await updateDoc(typedDoc<Notification>('notifications', id), {
    read: true,
  } as Partial<Notification>);
}

/** Mark ALL unread notifications for a user as read. */
export async function markAllRead(userId: string): Promise<void> {
  const snap = await getDocs(
    query(
      notifCol(),
      where('user_id', '==', userId),
      where('read', '==', false)
    )
  );
  if (snap.empty) return;
  const batch = writeBatch(db);
  for (const doc of snap.docs) {
    batch.update(doc.ref, { read: true });
  }
  await batch.commit();
}

/** Create a notification. Used by Cloud Functions and client-side triggers. */
export async function createNotification(
  userId: string,
  type: NotificationType,
  title: string,
  body: string,
  link?: string
): Promise<string> {
  const ref = await addDoc(notifCol(), {
    user_id: userId,
    type,
    title,
    body,
    read: false,
    created_at: serverTimestamp(),
    ...(link ? { link } : {}),
  } as Omit<Notification, 'id'>);
  return ref.id;
}
